import { useCallback, useEffect, useState } from "react";
import { ExternalLink, Landmark, Lock, Scale } from "lucide-react";

import { businessApi } from "../../../../services/api";
import Panel from "../../../components/Panel";
import {
  AGENCY_NAMES, formatDate, formatPesoCents, ruleFacts, rulePeriodText, ruleStatusLabel, VERIFICATION_LABELS,
} from "../statutoryView";

// Government rules Apparify uses (CD-S1). Read only: a business cannot change a government rule. Each rule shows
// where it comes from and how it was verified. Apparify's own calculation policies are listed apart from the
// government rules, so nobody mistakes one for the other.

const focusRing = "focus:outline-none focus-visible:ring-2 focus-visible:ring-neutral-900 focus-visible:ring-offset-2 "
  + "dark:focus-visible:ring-neutral-100 dark:focus-visible:ring-offset-neutral-900";

const VERIFICATION_STYLES = {
  ISSUING_AGENCY: "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300",
  OFFICIAL_GOVERNMENT_RECORD: "bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-300",
  GOVERNMENT_CORROBORATED: "bg-violet-100 text-violet-800 dark:bg-violet-900/40 dark:text-violet-300",
};

const FREQUENCY_NAMES = { monthly: "Monthly payroll", semi_monthly: "Semi-monthly payroll", weekly: "Weekly payroll" };

function WithholdingTables({ tables }) {
  return (
    <details className="mt-3">
      <summary className={`cursor-pointer text-sm font-medium text-neutral-700 dark:text-neutral-300 ${focusRing}`}>See the tax brackets</summary>
      <div className="mt-3 space-y-4">
        {Object.entries(tables).map(([frequency, brackets]) => (
          <div key={frequency} className="overflow-x-auto">
            <table className="min-w-full text-left text-sm tabular-nums">
              <caption className="pb-1.5 text-left text-xs font-semibold text-neutral-700 dark:text-neutral-300">{FREQUENCY_NAMES[frequency] || frequency}</caption>
              <thead className="text-xs text-neutral-500 dark:text-neutral-400">
                <tr>
                  <th scope="col" className="py-1 pr-4 font-medium">Taxable pay over</th>
                  <th scope="col" className="py-1 pr-4 font-medium">Tax</th>
                  <th scope="col" className="py-1 font-medium">Plus, on the amount over</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-200/70 text-neutral-800 dark:divide-neutral-800 dark:text-neutral-200">
                {brackets.map((bracket) => (
                  <tr key={bracket.bracketNo}>
                    <td className="py-1.5 pr-4">{formatPesoCents(bracket.lowerBoundCents)}</td>
                    <td className="py-1.5 pr-4">{formatPesoCents(bracket.baseTaxCents)}</td>
                    <td className="py-1.5">{bracket.rateBp ? `${bracket.rateBp / 100}%` : "No tax"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}
      </div>
    </details>
  );
}

function Rule({ rule, secondary = false }) {
  const verification = VERIFICATION_LABELS[rule.verificationLevel] || { label: "", explain: "" };
  const facts = ruleFacts(rule);
  return (
    <div className={secondary ? "text-neutral-700 dark:text-neutral-300" : ""}>
      <div className="flex flex-wrap items-center gap-2">
        <h5 className="text-sm font-semibold text-neutral-900 dark:text-neutral-50">{rule.sourceDocument}</h5>
        <span className="rounded-full bg-neutral-100 px-2 py-0.5 text-xs font-medium text-neutral-700 dark:bg-neutral-800 dark:text-neutral-300">
          {ruleStatusLabel(rule.status)}
        </span>
      </div>
      <p className="mt-0.5 text-sm text-neutral-600 dark:text-neutral-400">{rulePeriodText(rule)}</p>

      {facts.length ? (
        <dl className="mt-3 grid gap-x-6 gap-y-2 text-sm sm:grid-cols-2">
          {facts.map((fact) => (
            <div key={fact.label}>
              <dt className="text-xs text-neutral-500 dark:text-neutral-400">{fact.label}</dt>
              <dd className="text-neutral-900 dark:text-neutral-100">{fact.value}</dd>
            </div>
          ))}
        </dl>
      ) : null}

      {rule.details?.kind === "WITHHOLDING_TABLES" ? <WithholdingTables tables={rule.details.tables} /> : null}

      <div className="mt-3 rounded-xl bg-neutral-50 px-3 py-2.5 text-sm dark:bg-neutral-950/40">
        <div className="flex flex-wrap items-center gap-2">
          <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${VERIFICATION_STYLES[rule.verificationLevel] || ""}`}>
            {verification.label}
          </span>
          <span className="text-neutral-600 dark:text-neutral-400">{verification.explain}</span>
        </div>
        {rule.verificationNote ? <p className="mt-1.5 text-neutral-700 dark:text-neutral-300">{rule.verificationNote}</p> : null}
        <p className="mt-1.5 text-xs text-neutral-500 dark:text-neutral-400">
          {rule.issuingAuthority}{rule.documentDate ? `, ${formatDate(rule.documentDate)}` : ""}.{" "}
          <a href={rule.sourceUrl} target="_blank" rel="noopener noreferrer"
            className={`inline-flex items-center gap-1 font-medium text-neutral-800 underline underline-offset-2 dark:text-neutral-200 ${focusRing}`}>
            Open the source <ExternalLink className="h-3 w-3" aria-hidden="true" />
            <span className="sr-only">(opens in a new tab)</span>
          </a>
        </p>
      </div>
    </div>
  );
}

export default function StatutoryRulesPanel() {
  const [state, setState] = useState({ loading: true, error: "", restricted: false, data: null });

  const load = useCallback(async () => {
    setState((current) => ({ ...current, loading: true, error: "" }));
    try {
      const data = await businessApi.getStatutoryRules();
      setState({ loading: false, error: "", restricted: false, data });
    } catch (err) {
      if (err?.status === 403) setState({ loading: false, error: "", restricted: true, data: null });
      else setState({ loading: false, error: err?.serverMessage || "The government rules could not be loaded.", restricted: false, data: null });
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const { loading, error, restricted, data } = state;

  return (
    <div className="space-y-4">
      <Panel
        title="Government rules Apparify uses"
        subtitle="Set by Philippine law and the agencies. They are the same for every business and cannot be edited here."
        icon={Landmark}
      >
        {loading ? (
          <p className="p-4 text-sm text-neutral-500 dark:text-neutral-400">Loading the rules…</p>
        ) : restricted ? (
          <div className="flex items-start gap-3 p-4 text-sm text-neutral-700 dark:text-neutral-300">
            <Lock className="mt-0.5 h-4 w-4 shrink-0 text-neutral-500" aria-hidden="true" />
            <p>The government rules page is available to people who work with payroll or HR settings.</p>
          </div>
        ) : error ? (
          <div className="p-4">
            <p role="alert" className="text-sm text-red-700 dark:text-red-400">{error}</p>
            <button type="button" onClick={load}
              className={`mt-3 inline-flex min-h-9 items-center rounded-lg border border-neutral-300 px-3 text-sm font-medium text-neutral-700 hover:bg-neutral-50 dark:border-neutral-700 dark:text-neutral-200 dark:hover:bg-neutral-800 ${focusRing}`}>
              Try again
            </button>
          </div>
        ) : (
          <>
            <p className="border-b border-neutral-200/80 px-4 py-3 text-sm text-neutral-600 dark:border-neutral-800 dark:text-neutral-300">
              Payroll works out SSS, PhilHealth and Pag-IBIG contributions from these rules on the payslip that closes each month, and withholding tax on every payslip. A case the rules do not settle is held for HR to review before the payroll goes to Finance.
            </p>
            <ul className="divide-y divide-neutral-200/80 dark:divide-neutral-800">
              {data.agencies.map(({ agency, ruleSets }) => {
                const inUse = ruleSets.filter((rule) => rule.status === "in_use");
                const others = ruleSets.filter((rule) => rule.status !== "in_use");
                return (
                  <li key={agency} className="px-4 py-4" aria-label={AGENCY_NAMES[agency] || agency}>
                    <h4 className="text-base font-semibold text-neutral-900 dark:text-neutral-50">{AGENCY_NAMES[agency] || agency}</h4>
                    <div className="mt-3 space-y-4">
                      {inUse.length ? inUse.map((rule) => <Rule key={rule.versionKey} rule={rule} />) : (
                        <p className="text-sm text-amber-800 dark:text-amber-200">
                          No rule is recorded for today's date. Payroll that needs this rule will stop and say so instead of guessing.
                        </p>
                      )}
                    </div>
                    {others.length ? (
                      <details className="mt-4">
                        <summary className={`cursor-pointer text-sm font-medium text-neutral-700 dark:text-neutral-300 ${focusRing}`}>
                          Other periods ({others.length})
                        </summary>
                        <div className="mt-3 space-y-4 border-l border-neutral-200 pl-3 dark:border-neutral-800">
                          {others.map((rule) => <Rule key={rule.versionKey} rule={rule} secondary />)}
                        </div>
                      </details>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          </>
        )}
      </Panel>

      {data?.policies?.length ? (
        <Panel
          title="Apparify calculation policies"
          subtitle="Apparify's own rules for cases a government rule does not cover. They are not government rules."
          icon={Scale}
        >
          <ul className="divide-y divide-neutral-200/80 dark:divide-neutral-800">
            {data.policies.map((policy) => (
              <li key={policy.policyKey} className="px-4 py-4" aria-label={policy.title}>
                <div className="flex flex-wrap items-center gap-2">
                  <h4 className="text-sm font-semibold text-neutral-900 dark:text-neutral-50">{policy.title}</h4>
                  <span className="rounded-full bg-neutral-900 px-2 py-0.5 text-xs font-semibold text-white dark:bg-white dark:text-neutral-900">
                    Apparify policy
                  </span>
                </div>
                <p className="mt-1.5 max-w-prose text-sm text-neutral-800 dark:text-neutral-200">{policy.description}</p>
                <p className="mt-2 max-w-prose text-sm text-neutral-600 dark:text-neutral-400">{policy.provenanceNote}</p>
                <p className="mt-2 text-xs text-neutral-500 dark:text-neutral-400">
                  Applies to {AGENCY_NAMES[policy.agency] || policy.agency} contribution months from {formatDate(policy.appliesFrom)}. Adopted by Apparify on {formatDate(policy.adoptedOn)}.
                </p>
              </li>
            ))}
          </ul>
        </Panel>
      ) : null}
    </div>
  );
}
